import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useIsIntegrationContextEnabled,
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import { Stack, TextField } from '@mui/material';
import { IntegrationChannelType } from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';

export const EditPlaystoreIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationType,
  description,
}: EditIntegrationDrawerContentProps) => {
  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Playstore,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.Playstore,
          integrationID,
        });
      },
    });

  const [apiKeyJSON, setAPIKeyJSON] = useState<string | undefined>();
  const [descriptionInput, setDescriptionInput] = useState(description);

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidated,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Playstore,
    integrationType,
    hasValidFormFields: true, // apiKeyJSON is optional
    descriptionInput,
    currentDescription: description,
    onValidated: async () => {
      updateIntegration({
        integrationID,
        isActive: true,
        updateData: {
          playstore: {
            serviceAccountAPIKeyJSON: apiKeyJSON,
          },
        },
        updateDescription: {
          description: descriptionInput.trim(),
        },
      });
    },
  });

  const handleAPIKeyJSONChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAPIKeyJSON(event.target.value);
  };

  const isLoading = IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%">
      <AddOrEditIntegrationDrawerHeader title="Edit Playstore Integration" />
      <Stack justifyContent="space-between">
        <Stack p={3} spacing={2} justifyContent="space-between">
          <TextField
            fullWidth
            multiline
            maxRows={8}
            label="Service Account API Key JSON (Optional)"
            name="apiKeyJSON"
            placeholder="Enter Service Account API Key JSON"
            value={apiKeyJSON}
            disabled={isLoading}
            variant="outlined"
            onChange={handleAPIKeyJSONChange}
            helperText="Adding API Key allows Enterpret to fetch detailed metadata associated with each review."
          />
          {isIntegrationContextEnabled && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidated={isPreValidated}
              integrationType={integrationType}
            />
          )}
          {!isIntegrationContextEnabled && (
            <LoadingButton
              variant="contained"
              type="submit"
              size="large"
              fullWidth
              onClick={submitForm}
              loading={isLoading}
              disabled={isDisabled}
            >
              Save
            </LoadingButton>
          )}
        </Stack>
        {isIntegrationContextEnabled && (
          <AddOrEditIntegrationDrawerFooter
            onCancel={onDrawerClose}
            onSubmit={submitForm}
            isDisabled={isDisabled}
            isLoading={isLoading}
          />
        )}
      </Stack>
    </Stack>
  );
};
